import { Tab } from '@mui/material';
import { MprcContainer, MprcInfiniteLoading, MprcRequestErrorReport } from 'components';
import { MprcChipTabs } from 'components/MprcChipTabs';
import { DevPlanListViewMode, DevPlanPermissionResponse } from 'generated/CoreApi_v1';
import { useCoreApi } from 'providers/CoreApiProvider';
import { useSession } from 'providers/SessionProvider';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { LmsPaths } from 'routes/LmsRouter';
import { AllDevPlans } from 'views/lms/devPlan/Home/List/AllDevPlans';
import { MyDevPlans } from 'views/lms/devPlan/Home/List/MyDevPlans';
import { TeamDevPlans } from 'views/lms/devPlan/Home/List/TeamDevPlans';

////////////////////////////////////////////////////////////////////////////

type DevPlanTabOption = "personal" | "team" | "all";

interface DevPlanTab {
    title: string;
    type: DevPlanTabOption;
}

interface IProps {
    selectedOption: DevPlanTabOption;
}

////////////////////////////////////////////////////////////////////////////

export const DevPlanHome: React.FC<IProps> = (props) => {

    const api = useCoreApi();
    const { t } = useTranslation("lms");

    const navigate = useNavigate();
    const session = useSession();

    const [loading, setLoading] = useState<boolean>(true);
    const [tab, setTab] = useState<DevPlanTabOption>(props.selectedOption);
    const [error, setError] = useState<any>();
    const [permissions, setPermissions] = useState<DevPlanPermissionResponse>({
        canViewAllPlans: false,
        isManager: false
    });

    ///
    const tabs: DevPlanTab[] = useMemo(() => {
        const items: DevPlanTab[] = [];

        items.push({ title: t("DevPlan.List.MyPlansTabTitle"), type: "personal" });

        if (permissions.isManager)
            items.push({ title: t("DevPlan.List.TeamTabTitle"), type: "team" });

        if (permissions.canViewAllPlans)
            items.push({ title: t("DevPlan.List.AllPlansTabTitle"), type: "all" });

        return items;
    }, [t, permissions]);

    ////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        setLoading(true);
        api.v1.devPlan.getPermissions({})
            .then(response => setPermissions(response.data))
            .catch(e => setError(e))
            .finally(() => setLoading(false));
    }, []);

    return (
        <>
            {error
                ? <MprcRequestErrorReport error={error} />
                : loading
                    ? <MprcInfiniteLoading visible />
                    :
                    <>
                        {tabs.length > 1 &&
                            <MprcContainer margin="large">
                                <MprcChipTabs
                                    value={tab}
                                    onChange={(e, value) => setTab(value)}
                                >
                                    {tabs.map((item, i) =>
                                        <Tab
                                            key={i}
                                            label={item.title.toUpperCase()}
                                            value={item.type}
                                            onClick={() => {
                                                var route = LmsPaths.MyDevPlans;
                                                if (item.type == "all") route = LmsPaths.AllDevPlans;
                                                if (item.type == "team") route = LmsPaths.TeamDevPlans;

                                                navigate(route);
                                            }}
                                        />
                                    )}
                                </MprcChipTabs>
                            </MprcContainer>
                        }
                        {tab == "personal" && <MyDevPlans userId={session.user.userId} viewMode={DevPlanListViewMode.OwnUser} />}
                        {tab == "team" && <TeamDevPlans />}
                        {tab == "all" && <AllDevPlans />}
                    </>
            }

        </>
    );
};
